/// <reference types="@angular/localize" />

import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeNL from '@angular/common/locales/nl';
import {
    DEFAULT_CURRENCY_CODE,
    ErrorHandler,
    LOCALE_ID,
    enableProdMode,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideEnvironmentInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { LuxonDateModule, MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, Title, bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalModule,
    MsalService,
} from '@azure/msal-angular';
import { IPublicClientApplication, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { BffApiService } from '@raxapi/bff-api.service';
import { AppReleaseService } from '@raxapp/app-releases/app-release.service';
import { DarkModeService } from '@raxapp/appearance/dark-mode/dark-mode.service';
import { bookerBackendApiOverrideLuxonDurationMethods } from '@raxapp/booker-backend-apis/booker-backend-api-override-luxon-duration-methods.functions';
import { GlobalErrorHandler } from '@raxapp/error-handling/global-error-handler';
import { RouterEventSubscriptions } from '@raxapp/routing/router-event-subscriptions';
import { DateTime, Settings } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { CustomDateAdapter } from 'src/custom.date.adapter';
import { AppComponent } from './app/app.component';
import { AppRoutes } from './app/routing/app.routes';
import { environment } from './environments/environment';
import { EnvType } from './environments/iEnvironment';

function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication(environment.msalConfig);
}
function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, string[]>();

    protectedResourceMap.set(environment.msalProtectedResources.RaxBFFApi.endpoint, environment.msalProtectedResources.RaxBFFApi.scopes);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
        authRequest: {
            extraQueryParameters: {
                ui_locales: 'nl',
            },
        },
    };
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            extraQueryParameters: {
                ui_locales: 'nl',
            },
        },
        loginFailedRoute: '/login-failed',
    };
}

registerLocaleData(localeNL, 'nl');

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection(),
        provideAppInitializer(() => {
            const msalService = inject(MsalService);
            return firstValueFrom(msalService.initialize());
        }),
        provideEnvironmentInitializer(() => {
            Settings.defaultLocale = 'nl';
            Settings.defaultZone = 'Europe/Amsterdam';
            inject(DateAdapter<DateTime>).setLocale('nl');

            inject(AppReleaseService).logAppReleaseInfo();
            inject(BffApiService).bffClient.InitializeHeaders(environment.envName, inject(AppReleaseService).version);
            inject(DarkModeService).initializeDarkModeFromCachedValue();

            const routerEventSubscriptions = inject(RouterEventSubscriptions);
            routerEventSubscriptions.redirectToLoadingOnFirstPageLoad();
            routerEventSubscriptions.logNavigationError();

            bookerBackendApiOverrideLuxonDurationMethods();

            const titleService = inject(Title);
            console.log('env: ' + environment.envName);
            if (environment.envName !== EnvType.prod) {
                titleService.setTitle(environment.envName.toUpperCase() + ' ' + titleService.getTitle());
            }
        }),
        importProvidersFrom(MsalModule, MatDialogModule, MatSnackBarModule, MatLuxonDateModule, LuxonDateModule, BrowserModule),

        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        { provide: DateAdapter, useClass: CustomDateAdapter },

        { provide: APP_BASE_HREF, useValue: window.location.origin + '/' },
        { provide: MAT_DATE_LOCALE, useValue: 'nl' },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
        { provide: LOCALE_ID, useValue: 'nl' },
        { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
        { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
        { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
        { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: {
                disableClose: true,
                autoFocus: false,
                hasBackdrop: true,
            },
        },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideRouter(AppRoutes, withRouterConfig({ paramsInheritanceStrategy: 'always' }), withComponentInputBinding()),
    ],
}).catch((err) => console.error(err));
